import React, { useMemo, useState } from 'react';
import { isActiveOnDay } from '../utils/contentCalendar';
import { useT } from '../i18n';
import './ContentCalendarView.css';

const messages = {
  weekdays: { tr: ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'], en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] },
  prevMonth: { tr: 'Önceki ay', en: 'Previous month' },
  nextMonth: { tr: 'Sonraki ay', en: 'Next month' },
  today: { tr: 'Bugün', en: 'Today' },
  conflict: { tr: ' — aynı grubu hedefleyen başka bir içerikle çakışıyor', en: ' — overlaps with other content targeting the same group' },
  undated: { tr: 'Süresiz Aktif', en: 'Always Active' },
};

function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  // Monday-first grid: JS getDay() is 0=Sunday, shift so Monday=0.
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];
  for (let i = 0; i < leadingBlanks; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

// items: [{id, name, groups, start_date, end_date}] with start_date/
// end_date already parsed to Date|null. conflictIds: Set of ids to render
// with a warning style (see utils/contentCalendar.findScheduleConflicts).
// undatedItems: rendered separately below the grid (no start/end at all —
// "always on", not something a day-grid can represent).
export default function ContentCalendarView({ items, conflictIds, undatedItems = [], onSelectItem }) {
  const today = new Date();
  const { t, formatDate } = useT(messages);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const days = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const goPrevMonth = () => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNextMonth = () => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  return (
    <div className="content-calendar">
      <div className="content-calendar-nav">
        <button type="button" onClick={goPrevMonth} aria-label={t('prevMonth')}>‹</button>
        <span className="content-calendar-month-label">
          {formatDate(cursor, { month: 'long', year: 'numeric' })}
        </span>
        <button type="button" onClick={goNextMonth} aria-label={t('nextMonth')}>›</button>
        <button type="button" className="content-calendar-today-btn" onClick={goToday}>{t('today')}</button>
      </div>

      <div className="content-calendar-grid">
        {t('weekdays').map(label => (
          <div className="content-calendar-weekday" key={label}>{label}</div>
        ))}
        {days.map((day, idx) => {
          const dayItems = day ? items.filter(i => isActiveOnDay(i, day)) : [];
          const isToday = day && day.toDateString() === today.toDateString();
          return (
            <div className={`content-calendar-cell ${day ? '' : 'empty'} ${isToday ? 'today' : ''}`} key={idx}>
              {day && (
                <>
                  <span className="content-calendar-day-number">{day.getDate()}</span>
                  <div className="content-calendar-day-items">
                    {dayItems.map(i => (
                      <button
                        type="button"
                        key={i.id}
                        className={`content-calendar-chip ${conflictIds?.has(i.id) ? 'conflict' : ''}`}
                        title={i.name + (conflictIds?.has(i.id) ? t('conflict') : '')}
                        onClick={() => onSelectItem?.(i)}
                      >
                        {i.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {undatedItems.length > 0 && (
        <div className="content-calendar-undated">
          <h4>{t('undated')}</h4>
          <div className="content-calendar-day-items">
            {undatedItems.map(i => (
              <button type="button" key={i.id} className="content-calendar-chip" onClick={() => onSelectItem?.(i)}>
                {i.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
